import { useQuery } from "@tanstack/react-query";
import api from "../services/api";
import { userAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { replaceSavedDealsCache } from "../utils/userPreferences";

export const useSavedDeals = (options = {}) => {
  const {
    enabled = true, // Default to true - always enabled when authenticated to use cache
    staleTime = 30 * 60 * 1000, // 30 minutes default (longer cache)
    refetchOnMount = false, // Don't refetch if data exists (like deal list)
    refetchOnWindowFocus = false, // Don't refetch on window focus
    refetchOnReconnect = false, // Don't refetch on reconnect
    ...queryOptions
  } = options;
  const { isAuthenticated } = useAuth();

  const query = useQuery({
    queryKey: ["savedDeals"],
    queryFn: async () => {
      // Get saved deal IDs from backend
      const response = await userAPI.getSavedDeals();
      const savedDealIds = response.data?.savedDealIds || [];

      if (savedDealIds.length === 0) {
        replaceSavedDealsCache([]);
        return [];
      }

      // Filter out invalid IDs (must be non-empty strings)
      const validDealIds = savedDealIds.filter(
        (id) => typeof id === "string" && id.trim().length > 0
      );

      if (validDealIds.length === 0) {
        replaceSavedDealsCache([]);
        return [];
      }

      replaceSavedDealsCache(validDealIds);

      // Fetch deals by IDs
      const dealsResponse = await api.post("/deals/batch", {
        dealIds: validDealIds,
      });

      return dealsResponse.data || [];
    },
    enabled: enabled && isAuthenticated, // Always enabled when authenticated to use cache
    staleTime,
    gcTime: 60 * 60 * 1000, // 1 hour (keep in cache much longer)
    refetchOnMount,
    refetchOnWindowFocus,
    refetchOnReconnect,
    ...queryOptions, // Allow caller to override any other options
  });

  // Cache updates are handled by the always-mounted DealsTabsContainer listener.

  return {
    deals: query.data || [],
    loading: query.isLoading && !query.data, // Only loading if no data exists
    error: query.error?.response?.data?.message || query.error?.message || null,
    refetch: query.refetch,
  };
};
